/**
 * Skill telemetry emission — wraps skill execution with lifecycle spans.
 *
 * Creates start -> processing -> completion spans for each skill invocation.
 * Auto-detects skill version from persisted manifest.
 */
import { buildSkillInvocationSpan } from "@agentvault/crypto";
import type { TelemetrySpan, InstrumentationContext } from "@agentvault/crypto";

let spanBuffer: TelemetrySpan[] = [];

/**
 * Get and clear the buffered spans (for batch reporting).
 */
export function drainSkillSpans(): TelemetrySpan[] {
  const spans = spanBuffer;
  spanBuffer = [];
  return spans;
}

/**
 * Generate a unique invocation ID.
 */
function generateInvocationId(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  let hex = "";
  for (let i = 0; i < bytes.length; i++) {
    hex += bytes[i].toString(16).padStart(2, "0");
  }
  return hex;
}

/**
 * Wrap a skill execution with lifecycle telemetry spans.
 *
 * Creates two spans:
 * 1. start — emitted immediately when invocation begins
 * 2. completion — emitted when execution completes (success or error)
 *
 * @param skillName — name of the skill being executed
 * @param version — semver version of the skill
 * @param fn — the async function to execute
 * @param instrument — optional InstrumentationContext for direct reporting
 * @returns The result of fn()
 */
export async function wrapSkillExecution<T>(
  skillName: string,
  version: string | undefined,
  fn: () => Promise<T>,
  instrument?: InstrumentationContext,
): Promise<T> {
  const invocationId = generateInvocationId();
  const startTime = Date.now();

  // Emit start span
  const startSpan = buildSkillInvocationSpan({
    skillName,
    invocationId,
    phase: "start",
    version,
    latencyMs: 0,
    status: "ok",
  });

  if (instrument) {
    instrument.reportSkillInvocation({
      skillName,
      invocationId,
      phase: "start",
      version,
      latencyMs: 0,
      status: "ok",
    });
  } else {
    spanBuffer.push(startSpan);
  }

  try {
    const result = await fn();
    const latencyMs = Date.now() - startTime;

    // Emit completion span
    const completionSpan = buildSkillInvocationSpan({
      skillName,
      invocationId,
      phase: "completion",
      version,
      latencyMs,
      status: "ok",
    });

    if (instrument) {
      instrument.reportSkillInvocation({
        skillName,
        invocationId,
        phase: "completion",
        version,
        latencyMs,
        status: "ok",
      });
    } else {
      spanBuffer.push(completionSpan);
    }

    return result;
  } catch (err: unknown) {
    const latencyMs = Date.now() - startTime;
    const message = err instanceof Error ? err.message : String(err);

    // Emit error completion span
    const errorSpan = buildSkillInvocationSpan({
      skillName,
      invocationId,
      phase: "completion",
      version,
      latencyMs,
      status: "error",
      statusMessage: message,
    });

    if (instrument) {
      instrument.reportSkillInvocation({
        skillName,
        invocationId,
        phase: "completion",
        version,
        latencyMs,
        status: "error",
        statusMessage: message,
      });
    } else {
      spanBuffer.push(errorSpan);
    }

    throw err;
  }
}

/**
 * Report a completed skill invocation with full metadata.
 * Used for post-hoc reporting when the caller has already collected metrics.
 */
export function reportSkillInvocation(opts: {
  skillName: string;
  version?: string;
  latencyMs: number;
  success: boolean;
  inputTokens?: number;
  outputTokens?: number;
  costCents?: number;
  toolsUsed?: string[];
  schemaMatch?: boolean;
  errorMessage?: string;
}): TelemetrySpan {
  const span = buildSkillInvocationSpan({
    skillName: opts.skillName,
    invocationId: generateInvocationId(),
    phase: "completion",
    version: opts.version,
    latencyMs: opts.latencyMs,
    inputTokens: opts.inputTokens,
    outputTokens: opts.outputTokens,
    costCents: opts.costCents,
    toolsUsed: opts.toolsUsed,
    schemaMatch: opts.schemaMatch,
    status: opts.success ? "ok" : "error",
    statusMessage: opts.errorMessage,
  });

  spanBuffer.push(span);
  return span;
}
